import { Form } from 'react-bootstrap';

import { Info } from 'components/base/Info';
import { EditNumber } from 'components/forms/EditNumber';
import { Field } from 'components/forms/Field';

import { AutoRestartLimitsShell } from '../../edit/AutoRestartLimitsShell';
import { ReturnCodeChips } from '../../edit/ReturnCodeChips';
import { buildLoopEdgeRestartView, clampNonNegativeInt } from '../../edit/autoRestartView';
import { clampLoopEdgeRestartPatch, DEFAULT_LOOP_EDGE_RESTART_AMOUNT, readLoopEdgeRestartData, type LoopEdgeRestartData } from '../../edit/loopEdgeRestartData';
import { renderAutoRestartSummary } from '../../edit/renderAutoRestartSummary';

interface Props {
  readonly edgeId: string;
  readonly edgeType: string;
  readonly isSelfLoop: boolean;
  readonly data: unknown;
  readonly onChange: (next: LoopEdgeRestartData) => void;
  readonly disabled?: boolean;
}

/**
 * Loop-edge restart options inspector. Off/unset means unbounded looping (legacy), not "no retry".
 *
 * The "don't combine with node-definition automatic restart" warning states a backend constraint: a node that
 * auto-restarts retires its own outgoing loop edges. Kept to one line because the property panel is narrow.
 */
export const LoopEdgeRestartCard = ({ edgeId, edgeType, isSelfLoop, data, onChange, disabled = false }: Props) => {
  const options = readLoopEdgeRestartData(data);
  const enabled = !!options.auto_restart_enabled;
  const codes = options.restart_on_return_codes ?? [];
  const supportsDelay = edgeType !== 'error-loopback';
  const view = enabled
    ? buildLoopEdgeRestartView({
        amount: options.auto_restart_amount ?? 0,
        delaySeconds: supportsDelay ? (options.auto_restart_delay_seconds ?? 0) : 0,
        codes,
      })
    : null;

  const patch = (partial: Partial<LoopEdgeRestartData>) => {
    onChange({ ...options, ...clampLoopEdgeRestartPatch(partial) });
  };

  const onEnableChange = (checked: boolean) => {
    if (!checked) {
      patch({ auto_restart_enabled: false });
      return;
    }
    const amount = options.auto_restart_amount ?? 0;
    patch({
      auto_restart_enabled: true,
      auto_restart_amount: amount > 0 ? amount : DEFAULT_LOOP_EDGE_RESTART_AMOUNT,
    });
  };

  return (
    <div className="mini-editor-section">
      <Form.Group className="mb-3">
        <Form.Label>Edge</Form.Label>
        <Form.Control type="text" value={edgeId} disabled />
      </Form.Group>
      <Form.Group className="mb-3">
        <Form.Label>Type</Form.Label>
        <Form.Control type="text" value={isSelfLoop ? `${edgeType} (self-loop)` : edgeType} disabled />
      </Form.Group>

      <AutoRestartLimitsShell
        enabled={enabled}
        disabled={disabled}
        switchId={`loop-edge-restart-${edgeId}`}
        switchAriaLabel="Enable loop restart limits"
        title="Loop restart limits"
        subtitle={enabled ? 'Cap how often this loop edge restarts its target' : 'Unbounded while this edge keeps activating'}
        pillOnLabel="Limits on"
        pillOffLabel="Infinite"
        offNote="Loop restart limits are off — this edge loops without a max, delay, or return-code filter (current default)."
        onEnableChange={onEnableChange}
      >
        {view ? (
          <>
            <Info>
              {renderAutoRestartSummary(view.summary)} Gates <strong>loopback-driven</strong> restarts from this edge only.{' '}
              <strong>Don&apos;t combine with node-definition automatic restart.</strong>
            </Info>

            <Field
              label={
                <>
                  Only restart on return codes <span className="auto-restart__optional">(optional)</span>
                </>
              }
              helpText={
                <>
                  Leave empty to restart on <strong>any</strong> activation of this edge. Add exit codes to restrict.
                </>
              }
            >
              <ReturnCodeChips
                codes={codes}
                disabled={disabled}
                onChange={(v) => patch({ restart_on_return_codes: v })}
                ariaLabel="Only restart on return codes"
              />
            </Field>

            <div className="auto-restart__grid">
              <Field
                label="Max loop restarts"
                helpText="Counted per target node iteration (shared across loop edges into that node). 0 = no further loop restarts."
              >
                <EditNumber
                  value={options.auto_restart_amount ?? 0}
                  min={0}
                  disabled={disabled}
                  onChange={(v) => patch({ auto_restart_amount: clampNonNegativeInt(Number(v) || 0) })}
                />
              </Field>
              {supportsDelay ? (
                <Field label="Delay between retries (seconds)" helpText="Wait before the next iteration may dispatch">
                  <EditNumber
                    value={options.auto_restart_delay_seconds ?? 0}
                    min={0}
                    disabled={disabled}
                    onChange={(v) => patch({ auto_restart_delay_seconds: clampNonNegativeInt(Number(v) || 0) })}
                  />
                </Field>
              ) : (
                <Field label="Delay between retries" helpText="Not applied on error-loopback edges (recovery path has no clone delay).">
                  <Form.Control type="text" value="Not available for error-loopback" disabled />
                </Field>
              )}
            </div>
          </>
        ) : null}
      </AutoRestartLimitsShell>
    </div>
  );
};
